<template>
  <div>
    <Layout>
      <Loader :loading="loading" no-axios>
        <dashboard-header
            :date-range="selectedDateRange"
            :zone-id="selectedZoneId"
            class="layout-dashboard-header"
            @date-range-changed="onDateRangeChanged"
            @zone-id-changed="onZoneIdChanged"
        />
        <div class="row">
          <template v-if="fetchSucceed">
            <div class="col-md-12" style="margin-top: 75px">
              <div class="row g-20px">
                <div class="col-md-12">
                  <overview
                      :net-profit="totalNetProfit"
                      :quantity="totalQuantity"
                      :revenue="totalRevenue"
                  ></overview>
                </div>
              </div>
            </div>
            <div class="col-md-12">
              <div class="row g-20px">
                <div class="col-md-12">
                  <monthly-progress
                      :key="`monthly-progress-${formattedSelectedDates.join('-')}-${selectedZoneId.join(',')}-${loading}`"
                      :quantity-data="quantityMonthlyProgressChartData"
                      :quantity-options="quantityMonthlyProgressChartOptions"
                      :quantity-plugins="yearLabelPlugins"
                      :revenue-and-net-profit-data="revenueAndNetProfitMonthlyProgressChartData"
                      :revenue-and-net-profit-options="revenueAndNetProfitMonthlyProgressChartOptions"
                      :revenue-and-net-profit-plugins="revenueAndNetProfitMonthlyProgressChartPlugins"
                  />
                </div>
              </div>
            </div>
            <div class="row g-20px">
              <div class="col-md-12">
                <harvest-regional-performance
                    :key="`harvest-regional-performance-${formattedSelectedDates.join('-')}-${selectedZoneId.join(',')}-${loading}`"
                    :data="zoneMonthlySummaryData"
                ></harvest-regional-performance>
              </div>
            </div>
            <div class="col-md-12">
              <div class="row g-20px">
                <div class="col-md-12">
                  <monthly-harvest-size-distribution
                      :key="`monthly-harvest-summary-${formattedSelectedDates.join('-')}-${selectedZoneId.join(',')}-${loading}`"
                      :data="zoneMonthlyHarvestSummaryData"
                      :dates="selectedDateRange"
                      :zones="selectedZoneId"
                  />
                </div>
              </div>
            </div>
            <div class="row g-20px">
              <div class="col-md-12 col-lg-6">
                <top-processing
                    :key="`top-5-processing-${selectedDateRange[0]}-${selectedDateRange[1]}-${selectedZoneId.join(',')}-${zoneMonthlySummaryData.length}`"
                    :data="topCustomerPerformanceData"
                />
              </div>
              <div class="col-md-12 col-lg-6">
                <processing-performance
                    :key="`processing-performance-${formattedSelectedDates.join('-')}-${selectedZoneId.join(',')}-${loading}`"
                    :data="customerPerformanceData"
                />
              </div>
            </div>
          </template>
          <template v-else>
            <div class="col-12" style="margin-top: 75px">
              <b-card>
                <b-card-body class="d-flex align-items-center justify-content-center flex-column"
                             style="border: 1px solid #EAECF0; background-color: #F2F4F7; min-height: 200px">
                  <h4>{{
                      lodash.startCase($t('failed_to_fetch_{name}', {name: $t('performance_dashboards.singular')}))
                    }}</h4>
                  <p>{{ $t('performance_dashboards.failed_to_fetch_message') }}</p>
                  <b-button variant="primary" @click="$router.go(0)">{{ lodash.startCase($t('Refresh')) }}</b-button>
                </b-card-body>
              </b-card>
            </div>
          </template>
        </div>
      </Loader>
    </Layout>
  </div>
</template>

<script>
import ModelShow from "../models/show";
import Row from "@/components/models/show/custom-row";
import Loader from '@/components/widgets/loader'
import Overview from "@/router/views/dashboards/components/overview.vue";
import DashboardHeader from "@/router/views/dashboards/components/dashboard-header.vue";
import HarvestRegionalPerformance from "@/router/views/dashboards/components/harvest-regional-performance.vue";
import MonthlyProgress from "@/router/views/dashboards/components/monthly-progress.vue";
import MonthlyHarvestSizeDistribution from "@/router/views/dashboards/components/monthly-harvest-size-distribution.vue";
import ProcessingPerformance from "@/router/views/dashboards/components/processing-performance.vue";
import {emptyCenterTextPlugin, proportionalZeroAlignPlugin, yearLabelPlugin} from "@/utils/chartjs/graphPlugins";
import TopProcessing from "@/router/views/dashboards/components/top-processing.vue";

export default {
  extends: ModelShow,
  page: {
    title: "Dashboard",
  },
  components: {
    MonthlyHarvestSizeDistribution,
    DashboardHeader,
    HarvestRegionalPerformance,
    Overview,
    Loader,
    MonthlyProgress,
    ProcessingPerformance,
    TopProcessing,
  },
  props: {
    cardTitle: {
      default() {
        return this.lodash.startCase(this.$t("dashboards.singular"));
      },
    },
    resourceRoute: {default: "dashboards"},
    modelVariable: {default: "dashboard"},
    denyEdit: {type: Boolean, default: true},
    denyDelete: {type: Boolean, default: true},
    columns: {
      type: Object,
      default() {
        return {
          [this.modelVariable || "model"]: [
            {
              key: "key",
              label: this.$t("models.key"),
              component: Row,
            },
          ],
        };
      },
    },
  },
  data() {
    return {
      title: this.lodash.startCase(this.$t("dashboards.plural")),
      selectedZoneId: [1, 2, 3],
      selectedDate: 'Q1 2023',
      selectedDateRange: [this.$moment().startOf('years').toDate(), this.$moment().toDate()],
      dateRange: '2023-01-01~2023-12-31',
      topCustomerPerformanceData: [],
      customerPerformanceData: [],
      zoneSummaryData: [],
      zoneMonthlySummaryData: [],
      zoneMonthlyHarvestSummaryData: [],
      totalRevenue: 0,
      totalNetProfit: 0,
      totalQuantity: 0,
      fetchSucceed: false,
    };
  },
  computed: {
    formattedSelectedDates() {
      return this.selectedDateRange.map(date => this.$moment(date).format('DD MMMM YYYY'))
    },
    isSelectedDateInSameDay() {
      return this.$moment(this.selectedDateRange[0]).isSame(this.$moment(this.selectedDateRange[1]))
    },
    dateFilterParams() {
      return this.isSelectedDateInSameDay ? {
        'filter[ordered_at][=]': this.$moment(this.selectedDateRange[0]).format('YYYY-MM-DD'),
      } : {
        'filter[ordered_at][>=]': this.$moment(this.selectedDateRange[0]).format('YYYY-MM-DD'),
        'filter[ordered_at][<=]': this.$moment(this.selectedDateRange[1]).format('YYYY-MM-DD'),
      }
    },
    monthlyProgressMomentDates() {
      let momentStartDate = this.$moment(this.selectedDateRange[0]).startOf('months');
      let momentEndDate = this.$moment(this.selectedDateRange[1]).startOf('months');
      let momentCurrentDate = momentStartDate
      let data = []
      while (momentCurrentDate.isSameOrBefore(momentEndDate)) {
        data.push(momentCurrentDate.clone());
        momentCurrentDate.add(1, 'months');
      }

      return data
    },
    monthlyProgressData() {
      return this.monthlyProgressMomentDates.map(momentDate => {
        let currentZoneMonthlySummaryData = this.zoneMonthlySummaryData?.filter(({period}) => period === momentDate.format('YYYY-MM'))
        return {
          period: momentDate.format('YYYY-MM'),
          zones: currentZoneMonthlySummaryData.map(({zone_id, revenue, net_profit, quantity}) => ({
            zone_id: zone_id,
            name: `${this.$t('zones.singular')} ${zone_id}`,
            revenue: revenue,
            net_profit: net_profit,
            quantity: quantity
          })),
          revenue: currentZoneMonthlySummaryData.reduce((acc, {revenue}) => acc + revenue, 0),
          net_profit: currentZoneMonthlySummaryData.reduce((acc, {net_profit}) => acc + net_profit, 0),
          quantity: currentZoneMonthlySummaryData.reduce((acc, {quantity}) => acc + quantity, 0),
        }
      })
    },
    monthlyProgressChartLabels() {
      return this.monthlyProgressMomentDates.map(momentDate => momentDate.format('YYYY-MMM'))
    },
    revenueAndNetProfitMonthlyProgressChartData() {
      return {
        labels: this.monthlyProgressChartLabels,
        datasets: [
          {
            label: 'Net Profit',
            backgroundColor: '#FF9D0B',
            borderColor: '#FF9D0B',
            borderWidth: 3,
            fill: false,
            lineTension: 0.25,
            type: 'line',
            data: this.monthlyProgressData.map(({net_profit}) => net_profit),
            pointRadius: 0,
            pointStyle: 'none',
            yAxisID: 'y-axis-right',
            order: 1,
          },
          {
            label: 'Revenue',
            backgroundColor: '#4CB244',
            borderColor: '#4CB244',
            borderWidth: 3,
            data: this.monthlyProgressData.map(({revenue}) => revenue),
            yAxisID: 'y-axis-left',
            order: 2,
          },
        ],
        zoneData: this.monthlyProgressData.map(({zones}) => zones),
      }
    },
    revenueAndNetProfitMonthlyProgressChartOptions() {
      let revenues = this.monthlyProgressData.map(({revenue}) => revenue)
      let netProfits = this.monthlyProgressData.map(({net_profit}) => net_profit)
      let highestRevenue = Math.max(...revenues)
      if (highestRevenue < 100000000) highestRevenue = 100000000;
      let highestNetProfit = Math.max(...netProfits)
      if (highestNetProfit < 100000) highestNetProfit = 100000;
      let suggestedMaxRevenue = (highestRevenue / 1000000000).toFixed(2) * 1000000000
      let suggestMaxNetProfit = (highestNetProfit / 1000000).toFixed(2) * 1000000
      let lowestNetProfit = Math.min(...netProfits)
      let vm = this

      return {
        emptyState: {
          text: this.$t('menuitems.charts.no_data')
        },
        scales: {
          yAxes: [
            {
              id: 'y-axis-left',
              position: 'left',
              scaleLabel: {
                display: true,
                labelString: this.lodash.upperFirst(this.$t('currencies.billion')),
                fontColor: '#666',
                fontSize: 14
              },
              ticks: {
                beginAtZero: lowestNetProfit >= 0,
                callback: this.revenueAndNetProfitTickCallback,
                maxTicksLimit: 10,
                minTicksLimit: 7,
                suggestedMax: suggestedMaxRevenue,
              }
            },
            {
              id: 'y-axis-right',
              position: 'right',
              scaleLabel: {
                display: true,
                labelString: this.lodash.upperFirst(this.$t('currencies.million')),
                fontColor: '#666',
                fontSize: 14
              },
              ticks: {
                beginAtZero: lowestNetProfit >= 0,
                callback: this.netProfitTickCallback,
                maxTicksLimit: 10,
                minTicksLimit: 7,
                suggestedMax: suggestMaxNetProfit
              },
              gridLines: {
                drawOnChartArea: false
              }
            }
          ],
          xAxes: [{
            id: 'month-axis',
            gridLines: {
              display: false
            },
            ticks: {
              callback: function (value) {
                return value.split('-')[1];
              }
            },
            afterFit: function (scale) {
              scale.height += 30;
            }
          }]
        },
        responsive: true,
        maintainAspectRatio: false,
        hover: {
          mode: 'index',
          intersect: false
        },
        legend: {
          reverse: true,
          onClick: (e) => e.stopPropagation(),
        },
        tooltips: {
          enabled: false,
          backgroundColor: '#fff',
          mode: 'index',
          intersect: false,
          custom: function (tooltipModel) {
            var tooltipEl = document.getElementById('chartjs-tooltip');

            if (!tooltipEl) {
              tooltipEl = document.createElement('div');
              tooltipEl.id = 'chartjs-tooltip';
              tooltipEl.style.background = 'transparent';
              document.body.appendChild(tooltipEl);
            }

            if (tooltipModel.opacity === 0) {
              tooltipEl.style.opacity = 0;
              return;
            }

            let dataIndex = tooltipModel.dataPoints[0].index;
            const chartData = this._data;
            let label = chartData.labels[dataIndex];
            let netProfitDataset = chartData.datasets.find(dataset => dataset.label === 'Net Profit')
            let revenueDataset = chartData.datasets.find(dataset => dataset.label === 'Revenue')
            let tooltipTitleText = `${vm.lodash.startCase(vm.$t('dashboards.revenue'))} & ${vm.lodash.startCase(vm.$t('dashboards.net_profit'))}`
            let tooltipSubtitleText = `${vm.$moment(label, 'YYYY-MMM').format('MMMM YYYY')}`
            let zoneData = chartData.zoneData;
            let currZoneData = zoneData[dataIndex]
            let tooltipRowsHTML = vm.selectedZoneId.sort().map(zoneId => {
              let findZoneData = currZoneData.find(zone => zone.zone_id === zoneId)
              let label = `${vm.lodash.startCase(vm.$t('zones.singular'))} ${zoneId}`
              let revenue = vm.$numeral(findZoneData?.revenue ?? 0).format('$ 0,0.[00]')
              return `
                <div class="tooltip-row">
                  <div class="tooltip-label">${label}</div>
                  <div class="tooltip-value">${revenue}</div>
                  <div class="tooltip-indicator" style="background-color: ${revenueDataset.backgroundColor} !important;"></div>
                </div>`
            }).join('')
            let currentRevenue = revenueDataset.data[dataIndex]
            let currentNetProfit = netProfitDataset.data[dataIndex]

            let totalRevenueText = vm.$numeral(currentRevenue).format('$ 0,0.[00]')
            let netProfitText = vm.$numeral(currentNetProfit).format('$ 0,0.[00]')

            // Set HTML untuk tooltip
            tooltipEl.innerHTML = `
              <div class="tooltip-container">
                <div class="tooltip-header">
                  <div class="tooltip-title">${tooltipTitleText}</div>
                  <div class="tooltip-subtitle">${tooltipSubtitleText}</div>
                </div>
                <div class="tooltip-content">
                  ${tooltipRowsHTML}
                  <div class="tooltip-row">
                    <div class="tooltip-label">${vm.lodash.startCase(vm.$t('dashboards.total_revenue'))}</div>
                    <div class="tooltip-value">${totalRevenueText}</div>
                    <div class="tooltip-indicator" style="background-color: ${revenueDataset.backgroundColor} !important;"></div>
                  </div>
                  <div class="tooltip-row">
                    <div class="tooltip-label">${vm.lodash.startCase(vm.$t('dashboards.net_profit'))}</div>
                    <div class="tooltip-value">${netProfitText}</div>
                    <div class="tooltip-indicator" style="background-color: ${netProfitDataset.backgroundColor} !important;"></div>
                  </div>
                </div>
              </div>
            `;

            var position = this._chart.canvas.getBoundingClientRect();
            var cursorX = tooltipModel.caretX;

            tooltipEl.style.opacity = 1;
            tooltipEl.style.position = 'absolute';
            tooltipEl.style.pointerEvents = 'none';
            tooltipEl.style.background = 'transparent';

            setTimeout(() => {
              var tooltipWidth = tooltipEl.offsetWidth;
              var tooltipHeight = tooltipEl.offsetHeight;

              var horizontalPosition;
              var transformValue;
              var verticalOffset = 10;

              var viewportWidth = window.innerWidth;
              var tooltipRight = position.left + cursorX + tooltipWidth + 20;
              var tooltipLeft = position.left + cursorX - tooltipWidth - 20;

              if (tooltipRight > viewportWidth && tooltipLeft > 0) {
                horizontalPosition = position.left + window.pageXOffset + cursorX - 20 + 'px';
                transformValue = 'translate(-100%, 0)';
              } else {
                horizontalPosition = position.left + window.pageXOffset + cursorX + 20 + 'px';
                transformValue = 'translate(0, 0)';
              }

              var viewportHeight = window.innerHeight;
              var tooltipBottom = position.top + tooltipModel.caretY + tooltipHeight + verticalOffset;

              tooltipEl.style.left = horizontalPosition;
              tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + verticalOffset + 'px';
              tooltipEl.style.transform = transformValue;

              if (tooltipBottom > viewportHeight) {
                tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY - tooltipHeight - verticalOffset + 'px';
              }
            }, 0);
          }
        },
      }
    },
    revenueAndNetProfitMonthlyProgressChartPlugins() {
      return [yearLabelPlugin, proportionalZeroAlignPlugin, emptyCenterTextPlugin]
    },
    yearLabelPlugins() {
      return [yearLabelPlugin, emptyCenterTextPlugin]
    },
    quantityMonthlyProgressChartData() {
      return {
        labels: this.monthlyProgressChartLabels,
        datasets: [{
          label: 'Tonase Bongkar',
          backgroundColor: '#1B77DF',
          borderColor: '#1B77DF',
          borderWidth: 3,
          data: this.monthlyProgressData.map(({quantity}) => quantity),
          pointStyle: 'false',
        }],
        zoneData: this.monthlyProgressData.map(({zones}) => zones),
      }
    },
    quantityMonthlyProgressChartOptions() {
      let quantities = this.lodash.cloneDeep(this.monthlyProgressData.map(({quantity}) => quantity))
      let highestQuantity = Math.max(...quantities)
      if (highestQuantity < 100) highestQuantity = 100;
      let suggestedMaxQuantity = (highestQuantity / 1000).toFixed(2) * 1000
      let vm = this

      return {
        emptyState: {
          text: this.$t('menuitems.charts.no_data')
        },
        scales: {
          yAxes: [
            {
              id: 'y-axis-left',
              position: 'left',
              scaleLabel: {
                display: true,
                labelString: 'Ton',
                fontColor: '#666',
                fontSize: 14
              },
              ticks: {
                beginAtZero: true,
                callback: this.quantityTickCallback,
                maxTicksLimit: 7,
                minTicksLimit: 5,
                suggestedMax: suggestedMaxQuantity,
              }
            },
          ],
          xAxes: [{
            id: 'month-axis',
            gridLines: {
              display: false
            },
            ticks: {
              callback: function (value) {
                return value.split('-')[1];
              }
            },
            afterFit: function (scale) {
              scale.height += 30;
            }
          }],
        },
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          onClick: (e) => e.stopPropagation(),
        },
        hover: {
          mode: 'index',
          intersect: false
        },
        tooltips: {
          enabled: false,
          mode: 'index',
          intersect: false,
          custom: function (tooltipModel) {
            var tooltipEl = document.getElementById('chartjs-tooltip');

            if (!tooltipEl) {
              tooltipEl = document.createElement('div');
              tooltipEl.id = 'chartjs-tooltip';
              tooltipEl.style.background = 'transparent';
              document.body.appendChild(tooltipEl);
            }

            if (tooltipModel.opacity === 0) {
              tooltipEl.style.opacity = 0;
              return;
            }

            let dataIndex = tooltipModel.dataPoints[0].index;
            const chartData = this._data;
            let label = chartData.labels[dataIndex];
            let quantityDataset = chartData.datasets.find(dataset => dataset.label === 'Tonase Bongkar')
            let tooltipTitleText = `${vm.lodash.startCase(vm.$t('dashboards.unload_quantity'))}`
            let tooltipSubtitleText = `${vm.$moment(label, 'YYYY-MMM').format('MMMM YYYY')}`
            let zoneData = chartData.zoneData;
            let currZoneData = zoneData[dataIndex]
            let tooltipRowsHTML = vm.selectedZoneId.sort().map(zoneId => {
              let findZoneData = currZoneData.find(zone => zone.zone_id === zoneId)
              let label = `${vm.lodash.startCase(vm.$t('zones.singular'))} ${zoneId}`
              let quantity = vm.$numeral(findZoneData?.quantity ?? 0).format('0,0.[00]')
              return `
                <div class="tooltip-row">
                  <div class="tooltip-label">${label}</div>
                  <div class="tooltip-value">${quantity} kg</div>
                  <div class="tooltip-indicator" style="background-color: ${quantityDataset.backgroundColor} !important;"></div>
                </div>`
            }).join('')
            let currentQuantity = quantityDataset.data[dataIndex]
            let quantityLabel = vm.$t('Total {something}', {
              something: vm.lodash.startCase(vm.$t('dashboards.unload_quantity'))
            })
            let quantityText = vm.$numeral(currentQuantity).format('0,0.[00]')

            // Set HTML untuk tooltip
            tooltipEl.innerHTML = `
              <div class="tooltip-container">
                <div class="tooltip-header">
                  <div class="tooltip-title">${tooltipTitleText}</div>
                  <div class="tooltip-subtitle">${tooltipSubtitleText}</div>
                </div>
                <div class="tooltip-content">
                  ${tooltipRowsHTML}
                  <div class="tooltip-row">
                    <div class="tooltip-label">${quantityLabel}</div>
                    <div class="tooltip-value">${quantityText} kg</div>
                    <div class="tooltip-indicator" style="background-color: ${quantityDataset.backgroundColor} !important;"></div>
                  </div>
                </div>
              </div>
            `;

            var position = this._chart.canvas.getBoundingClientRect();
            var cursorX = tooltipModel.caretX;

            tooltipEl.style.opacity = 1;
            tooltipEl.style.position = 'absolute';
            tooltipEl.style.pointerEvents = 'none';
            tooltipEl.style.background = 'transparent';

            setTimeout(() => {
              var tooltipWidth = tooltipEl.offsetWidth;
              var tooltipHeight = tooltipEl.offsetHeight;

              var horizontalPosition;
              var transformValue;
              var verticalOffset = 10;

              var viewportWidth = window.innerWidth;
              var tooltipRight = position.left + cursorX + tooltipWidth + 20;
              var tooltipLeft = position.left + cursorX - tooltipWidth - 20;

              if (tooltipRight > viewportWidth && tooltipLeft > 0) {
                horizontalPosition = position.left + window.pageXOffset + cursorX - 20 + 'px';
                transformValue = 'translate(-100%, 0)';
              } else {
                horizontalPosition = position.left + window.pageXOffset + cursorX + 20 + 'px';
                transformValue = 'translate(0, 0)';
              }

              var viewportHeight = window.innerHeight;
              var tooltipBottom = position.top + tooltipModel.caretY + tooltipHeight + verticalOffset;

              tooltipEl.style.left = horizontalPosition;
              tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + verticalOffset + 'px';
              tooltipEl.style.transform = transformValue;

              if (tooltipBottom > viewportHeight) {
                tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY - tooltipHeight - verticalOffset + 'px';
              }
            }, 0);
          }
        }
      }
    }
  },
  watch: {
    selectedZoneId: {
      handler(newVal, oldVal) {
        if (this.lodash.isEqual(newVal, oldVal)) return;
        this.fetchData()
      },
      deep: true,
    },
    selectedDateRange: {
      handler(newVal, oldVal) {
        let isSameStartDate = this.$moment(newVal[0]).isSame(oldVal[0], 'day')
        let isSameEndDate = this.$moment(newVal[1]).isSame(oldVal[1], 'day')
        if (isSameStartDate && isSameEndDate) return;
        this.fetchData()
      },
      deep: true,
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    revenueAndNetProfitTickCallback(value) {
      return this.$numeral(value / 1000000000).format('0,0.[00]')
    },
    netProfitTickCallback(value) {
      return this.$numeral(value / 1000000).format('0,0.[00]')
    },
    revenueAndNetProfitTooltipLabel(tooltipItem, data) {
      const datasetLabel = data.datasets[tooltipItem.datasetIndex].label || ''
      const formattedValue = this.$numeral(tooltipItem.yLabel).format('$ 0,0.[00]')

      return `${datasetLabel}: ${formattedValue}`
    },
    quantityTickCallback(value) {
      return this.$numeral(value / 1000).format('0,0.[00]')
    },
    quantityTooltipLabel(tooltipItem, data) {
      const datasetLabel = data.datasets[tooltipItem.datasetIndex].label || ''
      const formattedValue = this.$numeral(tooltipItem.yLabel).format('0,0.[00]')

      return `${datasetLabel}: ${formattedValue} kg`
    },
    onZoneIdChanged(zoneId) {
      this.$set(this, 'selectedZoneId', zoneId,)
    },
    onDateRangeChanged(dateRange) {
      this.$set(this, 'selectedDateRange', dateRange);
    },
    getModelName() {
      return this.model["order_id"];
    },
    delete() {
      return this.$axios.delete(`/dashboards/${this.$route.params.key}`);
    },
    fetchData() {
      this.loading = true;
      return Promise.all([
        this.fetchTopCustomerPerformance(),
        this.fetchCustomerPerformance(),
        this.fetchZoneSummary(),
        this.fetchZoneMonthlySummary(),
        this.fetchZoneMonthlyHarvestSummary()
      ]).then((
          [
            fetchTopCustomerPerformanceRes,
            fetchCustomerPerformanceRes,
            fetchZoneSummaryRes,
            fetchZoneMonthlySummaryRes,
            fetchZoneMonthlyHarvestSummaryRes,
          ]
      ) => {
        this.setTopCustomerPerformanceData(fetchTopCustomerPerformanceRes)
        this.setCustomerPerformanceData(fetchCustomerPerformanceRes)
        this.setZoneSummaryData(fetchZoneSummaryRes)
        this.setZoneMonthlySummaryData(fetchZoneMonthlySummaryRes)
        this.setZoneMonthlyHarvestSummaryData(fetchZoneMonthlyHarvestSummaryRes)
        this.$set(this, 'fetchSucceed', true)
      }).catch(() => {
        this.$set(this, 'fetchSucceed', false)
      }).finally(() => {
        this.loading = false
      })
    },
    fetchModel() {
      this.loading = true;
      return this.$axios
          .get(`/dashboards/${this.$route.params.key}`, {
            params: {},
          })
          .then((response) => {
            const {
              data: {data},
            } = response;
            this.model = {
              ...data,
              ordered_at: data.ordered_at || data.estimated_harvest_date,
              purchase_items: data.purchase_items.map(function (item, index) {
                return {
                  ...item,
                  index: index + 1,
                  $hide: data.ticketable.ticket.customer_type === "brs",
                };
              }),
              operational_cost_document: data.files.filter((file) => file.name === 'operational cost document')?.[0],
              invoice_documents: data.files.filter((file) => file.name === 'invoice document'),
              down_payment_documents: data.files.filter((file) => file.name === 'down payment document'),
            };
            return response;
          })
          .catch((error) => this.failedAlert(error))
          .finally(() => (this.loading = false));
    },
    fetchTopCustomerPerformance() {
      return this.$axios.get(`/v1/customers/top`, {
        params: {
          'filter[zone_id]': this.selectedZoneId.join(','),
          ...this.dateFilterParams,
          per_page: 5,
        },
        timeout: 30000,
      })
    },
    fetchCustomerPerformance() {
      return this.$axios.get(`/v1/customers/performance`, {
        params: {
          'filter[zone_id]': this.selectedZoneId.join(','),
          ...this.dateFilterParams,
          per_page: 1000,
        },
        timeout: 30000,
      })
    },
    fetchZoneSummary() {
      return this.$axios.get(`/v1/sale_orders/summary/zone`, {
        params: {
          'filter[zone_id]': this.selectedZoneId.join(','),
          ...this.dateFilterParams,
        },
        timeout: 30000,
      })
    },
    fetchZoneMonthlySummary() {
      return this.$axios.get(`/v1/sale_orders/summary/zone/monthly`, {
        params: {
          'filter[zone_id]': this.selectedZoneId.join(','),
          ...this.dateFilterParams,
        },
        timeout: 30000,
      })
    },
    fetchZoneMonthlyHarvestSummary() {
      return this.$axios.get(`/v1/purchase_orders/summary/zone/monthly`, {
        params: {
          'filter[zone_id]': this.selectedZoneId.join(','),
          ...this.dateFilterParams,
        },
        timeout: 30000,
      })
    },
    setTopCustomerPerformanceData(res) {
      const {data: {data}} = res
      this.$set(this, 'topCustomerPerformanceData', data)
    },
    setCustomerPerformanceData(res) {
      const {data: {data}} = res
      this.$set(this, 'customerPerformanceData', data)
    },
    setZoneSummaryData(res) {
      const {data: {data, meta}} = res
      this.$set(this, 'zoneSummaryData', data)
      this.$set(this, 'totalRevenue', meta.total_revenue)
      this.$set(this, 'totalNetProfit', meta.total_net_profit)
      this.$set(this, 'totalQuantity', meta.total_quantity)
    },
    setZoneMonthlySummaryData(res) {
      const {data: {data}} = res
      this.$set(this, 'zoneMonthlySummaryData', data)
    },
    rangeOfQuarter(val) {
      const selectedQuarter = parseInt(val.split(' ')[0].replace('Q', ''));
      const selectedYear = parseInt(val.split(' ')[1]);

      const quarterStart = this.$moment().set({year: selectedYear, month: (selectedQuarter - 1) * 3, date: 1});
      const quarterEnd = this.$moment(quarterStart).endOf('quarter');

      return `${quarterStart.format('YYYY-MM-DD')}~${quarterEnd.format('YYYY-MM-DD')}`;
    },
    setZoneMonthlyHarvestSummaryData(res) {
      const {data: {data}} = res
      this.$set(this, 'zoneMonthlyHarvestSummaryData', data)
    }
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .preloader-component {
    background-color: #ffffff;
  }
}

.container-title {
  margin-top: 10px;
  font-family: Lato;
  font-size: 12px;
  font-weight: 800;
  line-height: 14px;
  letter-spacing: 0.05em;
  text-align: left;
  margin-bottom: 8px;
  text-transform: uppercase;
  font-family: 'Lato';
  color: #6C7589;
}

.custom-group {
  margin-bottom: 30px;
}

.custom-label,
.custom-text {
  font-family: Lato;
  font-size: 14px;

  line-height: 17px;
  letter-spacing: 0em;
  text-align: left;
}

.custom-label,
.label {
  font-weight: 700;
  color: #161b4d;
  text-transform: capitalize;
  margin-bottom: 10px;
}

.custom-text,
.value {
  font-weight: 400;
  color: #6c7589;
}

.value:not(.no-colon)::before {
  content: ": ";
}

.section-title {
  background-color: #f9fafb;
  color: #161b4d;
  padding: 14px 21px;
  text-transform: uppercase;
  font-family: Lato;
  font-size: 14px;
  font-weight: 700;
  line-height: 17px;
  letter-spacing: 0.05em;
  text-align: left;
  border: 1px solid #ececf2;
}

.section-title:first-of-type {
  border-top: none;
}

.section-body {
  padding: 14px 20px;

  .section-label,
  .section-value {
    font-family: Lato;
    font-size: 14px;
    line-height: 17px;
    letter-spacing: 0em;
    text-align: left;
  }

  .section-label {
    color: #161b4d;
    font-weight: 400;
  }

  .section-value {
    color: #161b4d;
    font-weight: 700;
    text-align: right;
  }

  .section-row {
    padding: 6px 0;
  }
}

.sale-item-thead {
  color: #161b4d;
  background-color: #f9fafb;
  text-transform: uppercase;
  font-family: Lato;
  font-size: 14px;
  font-weight: 700;
  line-height: 17px;
  letter-spacing: 0.05em;
  text-align: left;
}

.sale-item-tbody {
  color: #161b4d;
  font-family: Lato;
  font-size: 14px;
  font-weight: 400;
  line-height: 17px;
  letter-spacing: 0em;
  text-align: left;
}

::v-deep .page-content {
  background: #eef3f7;
}

$badge-background-primary: rgba(198, 239, 255, 0.3);
$badge-color-primary: #0084f3;
$badge-background-warning: #fff8e7;
$badge-color-warning: #ff9d0b;

.badge-container {
  &-base {
    background: $badge-background-primary;

    .stakeholder {
      color: #161b4d;
      font-weight: 700;
      font-size: 14px;
    }

    .name {
      font-weight: 700;
      font-size: 20px;
    }
  }

  &-primary {
    @extend .badge-container-base;

    .name {
      color: $badge-color-primary;
    }
  }

  &-warning {
    @extend .badge-container-base;
    background: $badge-background-warning;

    .name {
      color: $badge-color-warning;
    }
  }
}

::v-deep {
  .dashboard-card-header {
    font: 600 16px/24px 'Inter';
  }

  .overflow-hidden {
    overflow: hidden !important;
  }

  table {
    thead {
      background: #F9FAFB !important;
    }
  }
}
</style>